/**
 * 프롬프트 조립 파이프라인 — 결정 #12의 핵심 구현.
 *
 * 프리셋의 순서 있는 블록(system / persona / char-description / scenario /
 * lorebook / chat-history / author-note / post-history ...)을 위에서 아래로
 * 걸으며 각 블록의 내용을 채우고, 모든 텍스트는 Roqeet 인라인 값으로 전개한다.
 * 마지막에 토큰 예산 할당기가 chat-history를 컨텍스트 한도에 맞게 트림한다.
 *
 * 플랫폼 중립(결정 #10): 저장소/네트워크에 직접 접근하지 않는다. 호출부(web)가
 * 필요한 엔티티(캐릭터·페르소나·메시지·로어북)를 모두 로드해 넘긴다.
 */
import type {
  Character,
  Chat,
  Lorebook,
  Message,
  Persona,
  Preset,
  PromptBlock,
} from "../domain/index.js";
import type { ChatTurn, CompletionRequest } from "../ports/ai.js";
import { renderRoqeetText, type RoqeetRunContext } from "../roqeet/index.js";
import { scanLorebooks, type ActivationByPosition } from "./lorebook-scan.js";
import { heuristicTokenCount, type TokenCounter } from "./tokens.js";
import { measurePerformance } from "../perf/index.js";
import { stableStringify } from "../util/hash.js";

/** 파이프라인 입력. 호출부가 필요한 엔티티를 미리 해석(resolve)해 넘긴다. */
export interface BuildPromptInput {
  preset: Preset;
  /** 응답할 AI 캐릭터(1:1 기준; 그룹챗은 발화자별로 호출). */
  character: Character;
  /** 사용자 페르소나(없으면 @유저/@user는 빈 문자열). */
  persona?: Persona;
  chat: Chat;
  /** order 오름차순 메시지. excludedFromContext는 여기서 걸러진다. */
  messages: Message[];
  /** 캐릭터 임베드 + 채팅 레벨 로어북을 합쳐 넘긴다. */
  lorebooks?: Lorebook[];
  /** 호출 대상 모델 id. */
  model: string;
  stream: boolean;
  signal?: AbortSignal;
  /** Roqeet 인라인 식에서 읽을 추가 값. */
  values?: Record<string, unknown>;
  /** 토큰 카운터(미주입 시 휴리스틱). 정확 토크나이저는 lazy-load로 주입. */
  tokenCounter?: TokenCounter;
  /** 확률/랜덤 매크로용 난수원. 기본 Math.random. */
  random?: () => number;
  /**
   * 게임 모듈의 턴 프로토콜 지시문(결정 #25/#26). 구조화 게임 모듈이
   * protocolInstructions(moduleState)로 만든 문자열을 web 오케스트레이션이 넘긴다.
   * 시스템 컨텍스트 최상단에 별도 system 턴으로 삽입돼 모델에 태그 문법을 알린다.
   * 자유대화(롤플레잉)는 미지정 — 기존 동작 그대로(결정 #28 가산성).
   */
  moduleInstructions?: string;
  /**
   * 미리 계산된 로어북 활성화 결과(결정 #9/#12). 주입되면 buildPrompt가 내부 스캔을
   * 건너뛰고 이걸 그대로 쓴다. web이 무거운 스캔을 Web Worker로 오프로딩한 뒤 그
   * 결과를 넘길 때 사용한다(메인스레드 부하 절감). 미주입이면 기존대로 내부 스캔.
   */
  activation?: ActivationByPosition;
}

/** chat-history 경계를 기준으로 앞/뒤 고정 블록을 나눠 담는 내부 구조. */
interface Assembly {
  pre: ChatTurn[];
  post: ChatTurn[];
  /** chat-history 블록이 프리셋에 존재했는지. */
  hasHistory: boolean;
  /** at-depth 로어북 엔트리(히스토리 중간 삽입용). */
  atDepth: { depth: number; content: string }[];
}

type PromptChunkName =
  | "system_rules"
  | "character_profile"
  | "current_scene"
  | "memory_summary"
  | "recent_dialogue"
  | "tool_runtime_instructions"
  | "output_format_contract";

interface PromptChunkStats {
  hits: number;
  misses: number;
}

const PROMPT_CHUNK_CACHE_LIMIT = 5000;
const MAX_RECENT_DIALOGUE_TURNS = 1000;
const MAX_MEMORY_ENTRIES = 2000;
const promptChunkCache = new Map<string, string>();

/**
 * 프리셋 블록과 엔티티로부터 완성된 {@link CompletionRequest}를 만든다.
 * apiStyle === "chat"이면 messages(역할별 턴), "text"면 prompt(단일 문자열)를
 * 채운다.
 */
export function buildPrompt(input: BuildPromptInput): CompletionRequest {
  const chunkStats: PromptChunkStats = { hits: 0, misses: 0 };
  return measurePerformance(
    "assemble_prompt",
    () => ({
      inputSize: promptInputSize(input),
      fileId: input.chat.id,
      packageId: input.character.id,
      cache: input.activation ? "hit" : "miss",
      thread: "main",
      invalidationReason: input.activation ? "precomputed_memory_activation" : "inline_memory_activation",
      detail: {
        messages: input.messages.length,
        lorebooks: input.lorebooks?.length ?? 0,
        chunks: 7,
        chunkCacheHits: chunkStats.hits,
        chunkCacheMisses: chunkStats.misses,
        maxMemoryEntries: MAX_MEMORY_ENTRIES,
        maxRecentDialogueTurns: MAX_RECENT_DIALOGUE_TURNS,
      },
    }),
    () => buildPromptInternal(input, chunkStats),
  );
}

function buildPromptInternal(input: BuildPromptInput, chunkStats: PromptChunkStats): CompletionRequest {
  const { preset, character, persona, chat, model, stream } = input;
  const count = input.tokenCounter ?? heuristicTokenCount;
  const random = input.random ?? Math.random;

  const roqeet: RoqeetRunContext = {
    char: character.name,
    user: persona?.name ?? "",
    random,
    values: input.values,
  };
  const x = (s: string | undefined, chunk: PromptChunkName = "output_format_contract"): string =>
    s ? renderCachedPromptChunk(chunk, s, roqeet, chunkStats) : "";

  // 컨텍스트 히스토리(역할 매핑 + Roqeet 인라인 전개). system 발신은 모델에 보내지 않음.
  const liveMessages = input.messages
    .slice(-MAX_RECENT_DIALOGUE_TURNS)
    .filter((m) => !m.excludedFromContext);
  const historyTurns: ChatTurn[] = [];
  for (const m of liveMessages) {
    const content = x(m.variants[m.activeVariant]?.content ?? "", "recent_dialogue");
    if (!content) continue;
    if (m.senderType === "user") {
      historyTurns.push({ role: "user", content });
    } else if (m.senderType === "character" || m.senderType === "narrator") {
      const turn: ChatTurn = { role: "assistant", content };
      if (m.senderName) turn.name = m.senderName;
      historyTurns.push(turn);
    }
    // senderType === "system"은 엔진 공지이므로 컨텍스트에서 제외(결정 #9 주석).
  }

  // 로어북 스캔: 최근 메시지 창(scanDepth)을 합쳐 키워드 매칭(결정 #12). 미리 계산된
  // activation이 주입되면(web Worker 오프로딩) 그것을 쓰고, 아니면 내부 스캔한다.
  const activation = capActivation(input.activation ?? scanActivation(input, roqeet), MAX_MEMORY_ENTRIES);

  const a = assemble(preset.blocks, {
    x,
    character,
    persona,
    chat,
    activation,
  });

  // 게임 모듈 지시문(결정 #25/#26)을 시스템 컨텍스트 최상단에 삽입한다. 모델이
  // 본문 외에 어떤 RoQeet 게임 명령(@선택지/@설정/@주사위 …)을 쓸지 먼저 알도록 pre의 맨
  // 앞에 둔다. 모듈 지시문은 이미 web에서 구성된 상태로 들어온다.
  const moduleInstructions = input.moduleInstructions?.trim();
  if (moduleInstructions) {
    a.pre.unshift({
      role: "system",
      content: x(moduleInstructions, "tool_runtime_instructions"),
    });
  }

  // ---- 토큰 예산 할당기(결정 #12) ----
  const contextSize = preset.sampling.contextSize ?? 8192;
  const reserve = preset.sampling.maxTokens ?? 512;
  const budget = Math.max(0, contextSize - reserve);

  const fixedTokens =
    sumTokens(a.pre, count) + sumTokens(a.post, count) + atDepthTokens(a.atDepth, count);
  const historyBudget = Math.max(0, budget - fixedTokens);
  const trimmedHistory = trimToBudget(historyTurns, historyBudget, count);

  // at-depth 로어북을 히스토리 끝에서 depth만큼 앞에 삽입.
  const historyWithDepth = injectAtDepth(trimmedHistory, a.atDepth);

  const messages: ChatTurn[] = a.hasHistory
    ? [...a.pre, ...historyWithDepth, ...a.post]
    : [...a.pre, ...a.post, ...historyWithDepth];

  const request: CompletionRequest = {
    messages,
    model,
    params: preset.sampling,
    stream,
  };
  if (input.signal) request.signal = input.signal;

  // 텍스트 컴플리션 스타일: instructTemplate로 단일 문자열 렌더(결정 #12).
  if (preset.apiStyle === "text") {
    request.prompt = renderText(messages, preset);
  }

  return request;
}

function capActivation(
  activation: ActivationByPosition,
  maxEntries: number,
): ActivationByPosition {
  let remaining = Math.max(0, maxEntries);
  const out: ActivationByPosition = {
    "before-char": [],
    "after-char": [],
    "before-an": [],
    "after-an": [],
    "at-depth": [],
  };
  for (const key of Object.keys(out) as (keyof ActivationByPosition)[]) {
    if (remaining <= 0) break;
    const kept = activation[key].slice(0, remaining);
    out[key] = kept;
    remaining -= kept.length;
  }
  return out;
}

function renderCachedPromptChunk(
  name: PromptChunkName,
  source: string,
  roqeet: RoqeetRunContext,
  stats: PromptChunkStats,
): string {
  if (shouldBypassPromptChunkCache(source, roqeet)) {
    return renderRoqeetText(source, roqeet);
  }
  const key = stableStringify({
    name,
    source,
    char: roqeet.char ?? "",
    user: roqeet.user ?? "",
    values: roqeet.values ?? {},
    state: roqeet.state ?? {},
  });
  const cached = promptChunkCache.get(key);
  if (cached !== undefined) {
    stats.hits++;
    return cached;
  }
  stats.misses++;
  const rendered = renderRoqeetText(source, roqeet);
  promptChunkCache.set(key, rendered);
  if (promptChunkCache.size > PROMPT_CHUNK_CACHE_LIMIT) {
    const oldest = promptChunkCache.keys().next().value as string | undefined;
    if (oldest !== undefined) promptChunkCache.delete(oldest);
  }
  return rendered;
}

function shouldBypassPromptChunkCache(source: string, roqeet: RoqeetRunContext): boolean {
  return !!roqeet.now || /@(date|time|\uB0A0\uC9DC|\uC2DC\uAC04)(?![\p{L}\p{N}_])/u.test(source);
}

function promptInputSize(input: BuildPromptInput): number {
  let size = 0;
  size += input.character.name.length;
  size += input.character.description.length;
  size += input.character.personality?.length ?? 0;
  size += input.character.scenario?.length ?? 0;
  size += input.character.systemPrompt?.length ?? 0;
  size += input.persona?.description.length ?? 0;
  size += input.chat.authorNote?.length ?? 0;
  size += input.moduleInstructions?.length ?? 0;
  for (const message of input.messages) {
    size += message.variants[message.activeVariant]?.content.length ?? 0;
  }
  for (const book of input.lorebooks ?? []) {
    for (const entry of book.entries) {
      size += entry.content.length;
      size += entry.keys.join("").length;
      size += entry.secondaryKeys?.join("").length ?? 0;
    }
  }
  return size;
}

/** 최근 메시지 창을 스캔 텍스트로 만들어 로어북을 활성화한다. */
function scanActivation(input: BuildPromptInput, roqeet: RoqeetRunContext): ActivationByPosition {
  const count = input.tokenCounter ?? heuristicTokenCount;
  const books = input.lorebooks ?? [];
  if (books.length === 0) {
    return scanLorebooks([], "", input.random, count);
  }
  const scanText = buildLorebookScanText(input.messages, books, roqeet);
  // 토큰 예산(book.tokenBudget) 측정은 파이프라인과 동일 카운터로(결정 #12).
  return scanLorebooks(books, scanText, input.random, count);
}

/**
 * 로어북 스캔용 텍스트(가장 큰 scanDepth만큼의 최근 메시지 + Roqeet 인라인 전개)를 만든다.
 * web이 스캔을 Web Worker로 오프로딩할 때(결정 #9/#12), 함수가 못 넘는 Worker 경계를
 * 위해 메인스레드에서 이 문자열을 미리 만들어 보낸다(가벼운 작업).
 */
export function buildLorebookScanText(
  messages: BuildPromptInput["messages"],
  lorebooks: Lorebook[],
  roqeet: RoqeetRunContext,
): string {
  if (lorebooks.length === 0) return "";
  // 가장 큰 scanDepth만큼 최근 메시지를 합친다.
  const maxDepth = Math.max(...lorebooks.map((b) => b.scanDepth || 0), 1);
  const recent = messages.slice(-Math.min(maxDepth, MAX_RECENT_DIALOGUE_TURNS));
  return recent
    .map((m) => renderRoqeetText(m.variants[m.activeVariant]?.content ?? "", roqeet))
    .join("\n");
}

interface AssembleCtx {
  x: (s: string | undefined, chunk?: PromptChunkName) => string;
  character: Character;
  persona?: Persona;
  chat: Chat;
  activation: ActivationByPosition;
}

/** 블록을 순서대로 걸으며 chat-history 경계 기준으로 pre/post 턴을 만든다. */
function assemble(blocks: PromptBlock[], ctx: AssembleCtx): Assembly {
  const pre: ChatTurn[] = [];
  const post: ChatTurn[] = [];
  let seenHistory = false;
  let hasHistory = false;
  const atDepth: Assembly["atDepth"] = [];

  // at-depth 엔트리는 블록과 무관히 히스토리에 삽입.
  for (const { entry } of ctx.activation["at-depth"]) {
    atDepth.push({ depth: entry.depth ?? 0, content: ctx.x(entry.content, "memory_summary") });
  }

  const target = () => (seenHistory ? post : pre);
  const push = (role: ChatTurn["role"], content: string) => {
    const c = content.trim();
    if (c) target().push({ role, content: c });
  };

  for (const block of blocks) {
    if (!block.enabled) continue;
    const role = block.role ?? "system";

    switch (block.type) {
      case "system":
        push(role, ctx.x(block.content, "system_rules") || ctx.x(ctx.character.systemPrompt, "system_rules"));
        break;
      case "persona":
        if (ctx.persona) push(role, ctx.x(ctx.persona.description, "character_profile"));
        break;
      case "char-description": {
        const desc = ctx.x(ctx.character.description, "character_profile");
        const pers = ctx.x(ctx.character.personality, "character_profile");
        push(role, [desc, pers].filter(Boolean).join("\n\n"));
        break;
      }
      case "scenario":
        push(role, ctx.x(ctx.character.scenario, "current_scene"));
        break;
      case "example-dialogue":
        push(role, ctx.x(ctx.character.messageExample, "character_profile"));
        break;
      case "lorebook-before":
        push(role, joinEntries(ctx.activation["before-char"], ctx.x));
        break;
      case "lorebook-after":
        push(role, joinEntries(ctx.activation["after-char"], ctx.x));
        break;
      case "author-note": {
        // before-an / after-an 로어북을 author-note 주변에 배치.
        push(role, joinEntries(ctx.activation["before-an"], ctx.x));
        push(role, ctx.x(ctx.chat.authorNote, "current_scene"));
        push(role, joinEntries(ctx.activation["after-an"], ctx.x));
        break;
      }
      case "post-history":
        push(role, ctx.x(block.content, "output_format_contract") || ctx.x(ctx.character.postHistoryInstructions, "output_format_contract"));
        break;
      case "chat-history":
        seenHistory = true;
        hasHistory = true;
        break;
      case "custom":
        push(role, ctx.x(block.content, "output_format_contract"));
        break;
    }
  }

  return { pre, post, hasHistory, atDepth };
}

function joinEntries(
  entries: ActivationByPosition[keyof ActivationByPosition],
  x: (s: string | undefined, chunk?: PromptChunkName) => string,
): string {
  return entries.map((e) => x(e.entry.content, "memory_summary")).filter(Boolean).join("\n\n");
}

function sumTokens(turns: ChatTurn[], count: TokenCounter): number {
  return turns.reduce((n, t) => n + count(t.content) + 4, 0); // +4: 역할/구분 오버헤드 근사
}

function atDepthTokens(items: { content: string }[], count: TokenCounter): number {
  return items.reduce((n, i) => n + count(i.content) + 4, 0);
}

/** 최신 턴부터 예산 안에서 포함하고, 원래 순서를 유지해 반환한다. */
function trimToBudget(turns: ChatTurn[], budget: number, count: TokenCounter): ChatTurn[] {
  const kept: ChatTurn[] = [];
  let used = 0;
  for (let i = turns.length - 1; i >= 0; i--) {
    const t = turns[i]!;
    const cost = count(t.content) + 4;
    if (used + cost > budget && kept.length > 0) break;
    kept.push(t);
    used += cost;
  }
  return kept.reverse();
}

/** at-depth 엔트리를 히스토리 끝에서 depth 만큼 앞에 system 턴으로 삽입. */
function injectAtDepth(
  history: ChatTurn[],
  atDepth: { depth: number; content: string }[],
): ChatTurn[] {
  if (atDepth.length === 0) return history;
  const out = [...history];
  // 깊은 것부터 삽입해 인덱스 밀림을 단순화.
  const sorted = [...atDepth].sort((a, b) => a.depth - b.depth);
  for (const item of sorted) {
    if (!item.content.trim()) continue;
    const pos = Math.max(0, out.length - item.depth);
    out.splice(pos, 0, { role: "system", content: item.content.trim() });
  }
  return out;
}

/** 텍스트 컴플리션용 단일 문자열 렌더(결정 #12 instruct 포맷). */
function renderText(messages: ChatTurn[], preset: Preset): string {
  const t = preset.instructTemplate ?? {};
  const parts: string[] = [];
  for (const m of messages) {
    if (m.role === "system") {
      parts.push((t.systemPrefix ?? "") + m.content + (t.systemSuffix ?? "\n"));
    } else if (m.role === "user") {
      parts.push((t.userPrefix ?? "") + m.content + (t.userSuffix ?? "\n"));
    } else {
      parts.push((t.assistantPrefix ?? "") + m.content + (t.assistantSuffix ?? "\n"));
    }
  }
  // 모델이 어시스턴트로 이어쓰도록 마지막 프리픽스 추가.
  parts.push(t.lastAssistantPrefix ?? t.assistantPrefix ?? "");
  return parts.join("");
}
